// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
import { IMessageSender } from "@lib/messaging/IMessageSender";
import { NetworkType } from "@lib/messaging/NetworkType";
import { NetworkSource } from "@lib/messaging/NetworkSource";
import { IRemoteRpcHub } from "@lib/messaging/rpc/IRemoteRpcHub";
import { JsonRpcPendingCallContainer } from "@lib/messaging/rpc/jsonRpc/JsonRpcPendingCallContainer";
import { JsonRpcRequest } from "@lib/messaging/rpc/jsonRpc/JsonRpcRequest";
import { RpcHub } from "@lib/messaging/rpc/RpcHub";
import { CancellationToken } from "@lib/utilities/CancellationToken";
import { RpcResult } from "../RpcResult";

/**
 * Provides methods for calling remote JSON-RPC methods.
 */
export class JsonRpcRemoteRpcHub implements IRemoteRpcHub
{
  private readonly pendingCalls: JsonRpcPendingCallContainer<any, any>;
  private readonly messageSender: IMessageSender;

  constructor(pendingCalls: JsonRpcPendingCallContainer<any, any>, messageSender: IMessageSender)
  {
    this.pendingCalls = pendingCalls;
    this.messageSender = messageSender;
  }

  createClientHub(target: NetworkType | NetworkSource, cancellationToken: Optional<CancellationToken>): RpcHub
  {
    let networkSource: NetworkSource;
    if (typeof target === "string")
      networkSource = NetworkSource.create(target);
    else
      networkSource = target;

    return {
      invoke: <T, E>(method: string, args: any[], timeout?: number): RpcResult<T, E> =>
      {
        return this.invoke(networkSource, method, args, timeout, cancellationToken);
      },
      notify: (method: string, args: any[]): void =>
      {
        this.notify(networkSource, method, args);
      }
    }
  }

  invoke<T, E>(target: NetworkSource, method: string, args: any[], timeout?: number, cancellationToken?: CancellationToken): RpcResult<T, E>
  {
    const pendingCall = this.pendingCalls.add(target, method);

    const request: JsonRpcRequest = {
      jsonrpc: "2.0",
      id: pendingCall.id,
      method: method,
      params: args
    };

    this.messageSender.sendMessage(target, request);

    if (timeout !== undefined)
      pendingCall.cancelAfter(timeout);

    if (cancellationToken !== undefined)
      pendingCall.attachCancellation(cancellationToken);

    return pendingCall.promise;
  }

  notify(target: NetworkSource, method: string, args: any[]): void
  {
    const request: JsonRpcRequest = {
      jsonrpc: "2.0",
      method: method,
      params: args
    };

    this.messageSender.sendMessage(target, request);
  }
}